import { createAuthenticatedGraphQLClient } from '../../auth/createAuthenticatedGraphQLClient';
import { BulkCanvassingSessionTranscriptionStartStatus } from '../../graphql/__generated__/graphql';
import type { BulkCanvassingSessionTranscriptionFilter } from '../../graphql/__generated__/graphql';
import type {
  BulkTranscribeCanvassingSessionsMutation,
  BulkTranscribeCanvassingSessionsMutationVariables,
} from '../../graphql/queries/bulkTranscribeCanvassingSessions.generated';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import { GraphQLRequestError } from '../../graphql/routing/apiRouting';
import { BULK_TRANSCRIBE_CANVASSING_SESSIONS_DOCUMENT } from '../canvassingSessionDocuments';
import type { CanvassingSessionFetchImpl } from './canvassingSessionApi';

export type BulkCanvassingSessionTranscriptionError = {
  code: string | null;
  uncertain: boolean;
};

export type StartBulkCanvassingSessionTranscriptionOptions = {
  filter: BulkCanvassingSessionTranscriptionFilter;
  signal: AbortSignal;
  fetchImpl?: CanvassingSessionFetchImpl;
};

const readExtensionCode = (value: unknown): string | null => {
  if (typeof value !== 'object' || value === null) return null;
  const extensions = Reflect.get(value, 'extensions');
  if (typeof extensions !== 'object' || extensions === null) return null;
  const code = Reflect.get(extensions, 'code');
  return typeof code === 'string' ? code : null;
};

export const getBulkCanvassingSessionTranscriptionError = (error: unknown): BulkCanvassingSessionTranscriptionError => {
  if (!(error instanceof GraphQLRequestError)) return { code: null, uncertain: true };

  const codes = (error.graphQLErrors ?? []).map(readExtensionCode);
  if (error.status === 403 || codes.includes('FORBIDDEN')) return { code: 'FORBIDDEN', uncertain: false };
  const code = codes.find((candidate): candidate is string => candidate !== null) ?? null;

  if (error.kind === 'graphql') return { code, uncertain: false };
  return {
    code,
    uncertain: error.kind === 'empty-data' || error.status === undefined || error.status === 408 || error.status >= 500,
  };
};

export const bulkCanvassingSessionTranscriptionErrorCopy = (
  error: BulkCanvassingSessionTranscriptionError,
): string => {
  if (error.uncertain) {
    return 'The request may have started, but its result could not be confirmed. Check session details before trying again.';
  }

  switch (error.code) {
    case 'FORBIDDEN':
      return 'You do not have permission to view canvassing sessions.';
    case 'SESSION_TRANSCRIPTION_DISABLED':
      return 'Session transcription is disabled.';
    case 'CORE_CONFIGURATION_REQUIRED':
      return 'Core configuration is required.';
    case 'BULK_SESSION_TRANSCRIPTION_IN_PROGRESS':
      return 'Bulk processing is already running for this account on this server.';
    case 'BULK_SESSION_TRANSCRIPTION_UNAVAILABLE':
      return 'Bulk processing could not be started. Try again.';
    case 'BAD_USER_INPUT':
      return 'One or more filters are invalid. Review the date range, canvasser, capture status, processing status, and sort filters.';
    default:
      return 'Bulk processing could not be started. Try again.';
  }
};

export const startBulkCanvassingSessionTranscription = async ({
  filter,
  signal,
  fetchImpl,
}: StartBulkCanvassingSessionTranscriptionOptions): Promise<BulkCanvassingSessionTranscriptionStartStatus> => {
  const client = createAuthenticatedGraphQLClient({ env: resolveClientEnv(), fetchImpl });
  const variables: BulkTranscribeCanvassingSessionsMutationVariables = {
    filter,
  };
  const response = await client.request<BulkTranscribeCanvassingSessionsMutation>({
    document: BULK_TRANSCRIBE_CANVASSING_SESSIONS_DOCUMENT,
    variables,
    signal,
  });

  return response.bulkTranscribeCanvassingSessions.status;
};
